import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import Field from "@galaxy-io/dls/inputs/Field";
import { InputSize, InputVariant } from "@galaxy-io/dls/inputs/Input";
import TagInput, { TagInputVariant } from "@galaxy-io/dls/inputs/TagInput";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Box, { BoxVariant } from "@galaxy-io/dls/layout/Box";
import DlsLink from "@galaxy-io/dls/links/Link";
import InfiniteTable from "@galaxy-io/dls/table/InfiniteTable";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { Side } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { Integration } from "@gtm/contracts";
import type { RedditSource } from "@gtm/contracts/integrations/reddit";
import {
  ListMagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter, useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DrawerSection } from "@/components/workspace/drawer-content";
import { Stack } from "@/components/workspace/people-styles";
import { SourceIcon } from "@/components/workspace/source-icon";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { TableFooter } from "@/components/workspace/table-footer";
import { TableLayout } from "@/components/workspace/table-layout";
import { Toolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";
import { ConnectionSurface } from "./connection-surface";
import { IntegrationDrawerContent } from "./integration-drawer-content";
import { TrackingActions, useRemoveTrackedItem } from "./remove-tracked-item";
import { useConnectionReset } from "./use-connection-reset";

export function RedditConnection({
  open,
  selected,
  onClose,
  onSaved,
}: {
  open: boolean;
  selected?: Integration;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const removal = useRemoveTrackedItem<RedditSource>(
    "reddit",
    "subreddit",
    "reddit-sources",
    (row) => sourceLabel("reddit", row.subreddit),
  );
  const [name, setName] = useState(selected?.name ?? "Reddit");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const { params, update } = useUrlState();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { toast } = useToast();
  const sources = useQuery({
    queryKey: ["reddit-sources"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/integrations/reddit/sources", { signal })),
    enabled: open && !!selected,
  });
  useConnectionReset(open, !!selected, () => {
    setFormError(undefined);
    setName(selected?.name || "Reddit");
    setBusy(false);
  });
  async function action<T>(request: () => Promise<T>, header: string, subheader: string) {
    setFormError(undefined);
    setBusy(true);
    try {
      const result = await request();
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      await queryClient.invalidateQueries({ queryKey: ["reddit-sources"] });
      router.refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header,
        description: subheader,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      return result;
    } catch (error) {
      setFormError((error as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  }
  const tracked = sources.data?.filter((s) => s.integrationId === selected?.id) ?? [];
  // The search reads the subreddit and its keywords, the two things the row shows.
  const term = search.trim().toLowerCase();
  const rows = term
    ? tracked.filter(
        (source) =>
          source.subreddit.toLowerCase().includes(term) ||
          source.keywords.some((keyword) => keyword.toLowerCase().includes(term)),
      )
    : tracked;
  const currentEditing = tracked.find((s) => s.id === params.get("redditSource"));
  const adding = params.get("flow") === "add";
  const editing = useRetainedValue(currentEditing);
  return (
    <>
      {removal.dialog}
      <ConnectionSurface
        open={open}
        selected={selected}
        details={
          selected && (
            <DrawerSection header="Subreddits" icon={ListMagnifyingGlassIcon} defaultIsOpen isFlush>
              <Toolbar
                trailingActions={
                  <Button
                    label="Add subreddit"
                    variant={ButtonVariant.SECONDARY}
                    icon={PlusIcon}
                    isDisabled={busy || !selected.enabled}
                    onClick={() => update({ flow: "add", redditSource: null })}
                    size={ButtonSize.SMALL}
                  />
                }
              >
                <Input
                  aria-label="Search subreddits"
                  placeholder="Search subreddits, keywords…"
                  value={search}
                  onChange={(event) => setSearch(event)}
                  type="search"
                  size={InputSize.SMALL}
                  label="Search subreddits"
                  hideLabel
                />
              </Toolbar>
              <TableLayout
                fillHeight={false}
                footer={<TableFooter label="Tracked subreddits" total={rows.length} />}
              >
                {sources.isPending || sources.error ? (
                  <QueryState
                    error={sources.error}
                    loadingMessage="Loading subreddits…"
                    onRetry={() => void sources.refetch()}
                  />
                ) : (
                  <Box variant={BoxVariant.BASE}>
                    <InfiniteTable<RedditSource>
                      ariaLabel="Tracked subreddits"
                      canResizeColumns
                      data={rows}
                      getRowId={(source) => source.id}
                      isLoading={sources.isPending}
                      columns={[
                        {
                          id: "subreddit",
                          header: "Subreddit",
                          width: 160,
                          pin: Side.LEFT,

                          cell: ({ row }) => (
                            <DlsLink href={`https://www.reddit.com/r/${row.subreddit}/`} isExternal>
                              {sourceLabel("reddit", row.subreddit)}
                            </DlsLink>
                          ),
                          isRowHeader: true,
                        },
                        {
                          id: "keywords",
                          header: "Keywords",
                          minWidth: 200,

                          cell: ({ row }) => (
                            <Text size={TextSize.BODY_SM} lineClamp={1}>
                              {row.keywords.join(", ") || "All posts and comments"}
                            </Text>
                          ),
                        },
                        {
                          id: "action",
                          header: "",
                          // Two 28px icon buttons, their gap and the cell padding:
                          // the column hugs the controls instead of stranding them.
                          width: 96,
                          minWidth: 96,
                          maxWidth: 96,
                          canResize: false,
                          pin: Side.RIGHT,
                          align: "right" as const,

                          cell: ({ row }) => (
                            <TrackingActions>
                              <Button
                                icon={PencilSimpleIcon}
                                ariaLabel={`Edit ${sourceLabel("reddit", row.subreddit)}`}
                                variant={ButtonVariant.TERTIARY}
                                onClick={() =>
                                  update({
                                    redditSource: row.id,
                                    flow: null,
                                  })
                                }
                                tooltip={`Edit ${sourceLabel("reddit", row.subreddit)}`}
                              />
                              <Button
                                icon={TrashIcon}
                                ariaLabel={`Remove ${sourceLabel("reddit", row.subreddit)}`}
                                variant={ButtonVariant.TERTIARY}
                                isDisabled={removal.busy}
                                onClick={() =>
                                  removal.request(row.id, sourceLabel("reddit", row.subreddit))
                                }
                                tooltip={`Remove ${sourceLabel("reddit", row.subreddit)}`}
                              />
                            </TrackingActions>
                          ),
                        },
                      ]}
                      emptyState={
                        <EmptyState
                          header={search ? "No matching subreddits" : "No subreddits yet"}
                          description={
                            search
                              ? "Try another search."
                              : "Track a source to start collecting activity."
                          }
                        />
                      }
                    />
                  </Box>
                )}
              </TableLayout>
            </DrawerSection>
          )
        }
        flush
        title={selected?.name || "Connect Reddit"}
        headerLeading={<SourceIcon source="reddit" size={36} />}
        onClose={onClose}
        busy={busy}
        dirty={name !== (selected?.name || "Reddit")}
        footer={
          <Button
            label={selected ? "Save connection" : "Connect Reddit"}
            isDisabled={busy || !name.trim()}
            isLoading={busy}
            onClick={async () => {
              const result = await action(
                () =>
                  selected
                    ? unwrap(
                        apiClient().PATCH("/integrations/reddit/connections/{id}", {
                          params: { path: { id: selected.id } },
                          body: { name, enabled: selected.enabled },
                        }),
                      )
                    : unwrap(
                        apiClient().POST("/integrations/reddit/connections", {
                          body: { name },
                        }),
                      ),
                "Reddit connection saved",
                "Public posts are collected via Arctic Shift.",
              );
              if (result) onSaved(result.id);
            }}
          />
        }
      >
        {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
        <IntegrationDrawerContent>
          <Stack>
            <TextInput
              label="Integration name"
              ref={(el) => el?.setAttribute("aria-label", "Integration name")}
              value={name}
              onChange={setName}
              variant={InputVariant.BASE}
              fillWidth
              isDisabled={busy}
            />
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              Public posts and comments. No credentials required.
            </Text>
          </Stack>
        </IntegrationDrawerContent>
      </ConnectionSurface>
      {selected && (
        <RedditSourceForm
          key={adding ? "new" : (editing?.id ?? "new")}
          open={open && (adding || !!currentEditing)}
          source={adding ? undefined : editing}
          busy={busy}
          onClose={() => update({ flow: null, redditSource: null })}
          onSave={async (input) => {
            const result = await action<{ ok: boolean } | { id: string }>(
              () =>
                !adding && editing
                  ? unwrap(
                      apiClient().PUT("/integrations/reddit/sources/{id}", {
                        params: { path: { id: editing.id } },
                        body: input,
                      }),
                    )
                  : unwrap(
                      apiClient().POST("/integrations/reddit/sources", {
                        body: { ...input, integrationId: selected.id },
                      }),
                    ),
              !adding && editing ? "Tracking settings saved" : "Subreddit added",
              !adding && editing
                ? `Collection for ${sourceLabel("reddit", editing.subreddit)} uses the new keywords.`
                : "Run collection from Workflows to start ingesting.",
            );
            if (result) update({ flow: null, redditSource: null });
          }}
        />
      )}
    </>
  );
}
function RedditSourceForm({
  open,
  source,
  busy,
  onClose,
  onSave,
}: {
  open: boolean;
  source?: RedditSource;
  busy: boolean;
  onClose: () => void;
  onSave: (input: {
    subreddit: string;
    keywords: string[];
    since: string;
    enabled: boolean;
  }) => Promise<void>;
}) {
  const [subreddit, setSubreddit] = useState(source?.subreddit ?? "");
  const [keywords, setKeywords] = useState(source?.keywords ?? []);
  const [since, setSince] = useState(
    () =>
      source?.since.slice(0, 10) ?? new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
  );
  useDialogReset(open, () => {
    setSubreddit(source?.subreddit ?? "");
    setKeywords(source?.keywords ?? []);
    setSince(
      source?.since.slice(0, 10) ?? new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    );
  });
  const [today] = useState(() => new Date().toISOString().slice(0, 10));
  return (
    <SurfaceDialog
      open={open}
      title={source ? `Edit ${sourceLabel("reddit", source.subreddit)}` : "Track subreddit"}
      onClose={onClose}
      busy={busy}
      dirty={
        subreddit !== (source?.subreddit ?? "") ||
        JSON.stringify(keywords) !== JSON.stringify(source?.keywords ?? []) ||
        (!!source && since !== source.since.slice(0, 10))
      }
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={busy}
            onClick={onClose}
          />
          <Button
            label={source ? "Save tracking" : "Track subreddit"}
            isLoading={busy}
            isDisabled={busy || !subreddit.trim() || !since}
            onClick={() =>
              onSave({
                subreddit,
                keywords,
                since: new Date(`${since}T00:00:00Z`).toISOString(),
                enabled: true,
              })
            }
          />
        </>
      }
    >
      <Stack>
        {source && (
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            Last collected: {date(source.lastSyncAt, true)}
          </Text>
        )}
        <TextInput
          label="Subreddit"
          ref={(el) => el?.setAttribute("aria-label", "Subreddit")}
          placeholder="r/PostgreSQL"
          value={source ? sourceLabel("reddit", subreddit) : subreddit}
          onChange={setSubreddit}
          isDisabled={busy || !!source}
          variant={InputVariant.BASE}
          fillWidth
        />
        <TagInput
          label="Keywords or phrases"
          ref={(el) => el?.setAttribute("aria-label", "Keywords or phrases")}
          placeholder="Add a keyword or phrase"
          separators={["\n"]}
          value={keywords}
          onChange={setKeywords}
          isDisabled={busy}
          variant={TagInputVariant.BASE}
          fillWidth
        />
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          Press Enter to add a phrase. Leave empty to collect all posts and comments.
        </Text>
        <Field label="Import from (UTC)">
          <Input
            aria-label="Import from (UTC)"
            type="date"
            value={since}
            max={today}
            disabled={busy}
            onChange={(e) => setSince(e)}
          />
        </Field>
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          Import available history from this date. Archive coverage may be delayed.
        </Text>
      </Stack>
    </SurfaceDialog>
  );
}
